import {useContext, useEffect, useState} from 'react'
import {Col, Flex, Form, Modal, Row, Typography} from 'antd'
import {useIntl} from 'react-intl'
import {getRequest, postRequest, putRequest} from '../../../../services/base'
import {NavContext} from '../../../../context/global_context'
import useDebouncedValue from '../../../../utils/common_function'
import {SET_SHOW_SHIFT_POPUP, ShiftContext} from '../context/context'
import InputText from '../../../common/InputText'
import SelectInput from '../../../common/SelectInput'
import Button from '../../../common/Button'

const TIME_PATTERN = '^\\d{2}:\\d{2}$'

const ShiftFormModal = (props) => {
  const intl = useIntl()
  const [state, dispatch] = useContext(ShiftContext)
  const [, navDispatch] = useContext(NavContext)
  const [form] = Form.useForm()

  const [plantOptions, setPlantOptions] = useState([])
  const [plantSearchFailed, setPlantSearchFailed] = useState(false)
  const [plantSearch, setPlantSearch] = useState('')
  const [plantLoading, setPlantLoading] = useState(false)
  const debouncedPlantSearch = useDebouncedValue(plantSearch, 500)

  const selected = state?.selected_shift || {}

  const closeFormPopup = () => {
    dispatch({ type: SET_SHOW_SHIFT_POPUP, data: false })
    form.resetFields()
  }

  const getInitialValues = () => ({
    is_create: !selected?.id,
    id: selected?.id || undefined,
    plant: selected?.plant
      ? { value: selected.plant.id, label: selected.plant.name }
      : undefined,
    name: selected?.name || '',
    start_time: selected?.start_time || '',
    end_time: selected?.end_time || '',
  })

  const fetchPlants = async (searchTerm = '') => {
    setPlantLoading(true)
    try {
      let res = await getRequest(`/api/plants/?search=${searchTerm}`)
      // Search-as-you-type: shown in the dropdown, not as a toast per keystroke.
      if (res?.status === false) {
        setPlantSearchFailed(true)
        setPlantOptions([])
        return
      }
      setPlantSearchFailed(false)
      const results = res?.results || []
      setPlantOptions(
        results.map((item) => ({ value: item.id, label: item.name }))
      )
    } finally {
      setPlantLoading(false)
    }
  }

  useEffect(() => {
    if (!state.show_shift_popup) return
    fetchPlants('')
  }, [state.show_shift_popup])

  useEffect(() => {
    if (debouncedPlantSearch !== undefined) {
      fetchPlants(debouncedPlantSearch)
    }
  }, [debouncedPlantSearch])

  useEffect(() => {
    if (!state.show_shift_popup) return
    form.setFieldsValue(getInitialValues())
  }, [state.show_shift_popup])

  const saveShift = async (values) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'shifts.save' }),
        showLoader: true,
      },
      notification: {},
    })

    const formData = new FormData()
    Object.entries(values).forEach(([key, value]) => {
      if (value && typeof value === 'object' && value.value !== undefined) {
        formData.append(key, value.value)
      } else if (value !== undefined && value !== null) {
        formData.append(key, value)
      }
    })

    let res = null
    if (selected?.id) {
      res = await putRequest(`/api/shift/${selected.id}/`, formData)
    } else {
      res = await postRequest(`/api/shift/`, formData)
    }

    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })

    if (res?.status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({
            id: selected?.id
              ? 'shifts.updateSuccess'
              : 'shifts.saveSuccess',
          }),
        },
      })
      props.getShiftListData()
      closeFormPopup()
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'shifts.saveError' }),
        },
      })
    }
  }

  return (
    <Modal
      open={state.show_shift_popup}
      title={
        selected?.id
          ? intl.formatMessage({ id: 'shifts.updateTitle' })
          : intl.formatMessage({ id: 'shifts.createTitle' })
      }
      width="50%"
      className="shifts-form-modal"
      centered={true}
      footer={null}
      closable={true}
      onCancel={closeFormPopup}
    >
      <div className="container">
        <Form
          id="shift-form"
          name="shift-form"
          form={form}
          layout="vertical"
          onFinish={saveShift}
        >
          <InputText name="is_create" hidden={true} isRequired={false} />
          <InputText name="id" hidden={true} isRequired={false} />
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <SelectInput
                name="plant"
                label={intl.formatMessage({ id: 'shifts.selectPlant' })}
                options={plantOptions}
                placeholder={intl.formatMessage({ id: 'shifts.selectPlant' })}
                isRequired={true}
                allowClear={true}
                showSearch={true}
                showLoader={plantLoading}
                onInputChange={(value) => setPlantSearch(value || '')}
                notFoundContent={
                  plantSearchFailed ? (
                    <Typography.Text type="danger" style={{ color: 'var(--color-form-link-danger)' }}>
                      {intl.formatMessage({ id: 'generic.searchFailed' })}
                    </Typography.Text>
                  ) : undefined
                }
              />
            </Col>
            <Col span={24}>
              <InputText
                name="name"
                label={intl.formatMessage({ id: 'shifts.name' })}
                isRequired={true}
              />
            </Col>
            <Col span={24}>
              <InputText
                name="start_time"
                label={intl.formatMessage({ id: 'shifts.startTime' })}
                placeholder="HH:MM"
                pattern={TIME_PATTERN}
                isRequired={true}
                errorHelp={true}
                rules={[
                  { required: true, message: '' },
                  {
                    pattern: new RegExp(TIME_PATTERN),
                    message: intl.formatMessage({
                      id: 'shifts.timeFormatError',
                    }),
                  },
                ]}
              />
            </Col>
            <Col span={24}>
              <InputText
                name="end_time"
                label={intl.formatMessage({ id: 'shifts.endTime' })}
                placeholder="HH:MM"
                pattern={TIME_PATTERN}
                isRequired={true}
                errorHelp={true}
                rules={[
                  { required: true, message: '' },
                  {
                    pattern: new RegExp(TIME_PATTERN),
                    message: intl.formatMessage({
                      id: 'shifts.timeFormatError',
                    }),
                  },
                ]}
              />
            </Col>
            <Col span={24}>
              <Flex justify="end" className="py-4">
                <Button htmlType="submit" type="primary">
                  {intl.formatMessage({ id: 'shifts.save' })}
                </Button>
              </Flex>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  )
}

export default ShiftFormModal
